/**
 * Product category taxonomy (HQ catalog).
 *
 * Reproduces Alo Yoga's shop structure (aloyoga.com main nav, fetched
 * 2026-09-03): Women / Men / Accessories / Shoes → categories. The
 * founder's brief was "missing categories — look at aloyoga.com", so the
 * groups + labels follow that nav; the one deliberate deviation is
 * "Skirts & Skorts" (Alo says "Skirts") because EQL's own line is skort-
 * heavy and the team calls them skorts.
 *
 * `key` is what gets persisted in `Product.categoryKey` and what rides in
 * storefront URLs (`/products?category=leggings`) — treat it as a contract:
 * rename `en` / `vi` labels freely, never a key.
 *
 * Pure module — no server imports, no React — so it is safe in loaders,
 * actions, client components, tests and the storefront `meta` function.
 */

export type CategoryGroupKey = "women" | "men" | "accessories" | "shoes";

export interface CategoryGroup {
  key: CategoryGroupKey;
  en: string;
  vi: string;
}

export interface Category {
  /** Stable slug stored in Product.categoryKey. */
  key: string;
  group: CategoryGroupKey;
  en: string;
  vi: string;
  /**
   * Title patterns (EN + VI) that confidently identify this category.
   * Matched in SUGGEST_ORDER priority — first hit wins.
   */
  keywords: RegExp[];
  /**
   * Generic patterns ("áo", "quần") only consulted when NO category's
   * `keywords` matched. Produce a low-confidence suggestion.
   */
  weakKeywords?: RegExp[];
}

export const CATEGORY_GROUPS: readonly CategoryGroup[] = [
  { key: "women", en: "Women", vi: "Nữ" },
  { key: "men", en: "Men", vi: "Nam" },
  { key: "accessories", en: "Accessories", vi: "Phụ kiện" },
  { key: "shoes", en: "Shoes", vi: "Giày dép" },
];

// ─── Keyword helpers ────────────────────────────────────────────────
// JS `\b` is ASCII-only: "\bđầm\b" never matches because "đ" is not a \w
// character, so there is no word boundary between a space and "đ". Build
// Unicode-aware boundaries with lookarounds on \p{L}\p{N} instead.
const NOT_BEFORE = "(?<![\\p{L}\\p{N}])";
const NOT_AFTER = "(?![\\p{L}\\p{N}])";

/**
 * Whole-word, case-insensitive, Unicode-aware alternation. Terms may use
 * regex syntax (`shorts?`, `t-shirts?`, `shorts?(?!\\s*sleeve)`).
 */
export function kw(...terms: string[]): RegExp {
  return new RegExp(`${NOT_BEFORE}(?:${terms.join("|")})${NOT_AFTER}`, "iu");
}

// Shared garment vocabularies (women's and men's categories reuse them).
const KW_SWEATSHIRTS = kw("hoodies?", "hooded", "sweatshirts?", "crewnecks?", "pullovers?", "áo nỉ", "áo hoodie");
const KW_SWEATPANTS = kw("sweatpants?", "joggers?", "sweats", "track pants?", "quần nỉ", "quần jogger");
const KW_JACKETS = kw(
  "jackets?", "coats?", "parkas?", "puffers?", "windbreakers?", "blazers?", "vests?", "anoraks?", "trench",
  "áo khoác", "áo gió", "áo phao",
);
// "short sleeve" is a top, not shorts.
const KW_SHORTS = kw("shorts?(?!\\s*-?\\s*sleeves?)", "quần short", "quần sooc", "quần soóc", "quần đùi", "quần ngắn");
const KW_PANTS = kw(
  "pants?", "trousers?", "flares?", "flared", "wide[- ]leg", "straight[- ]leg",
  "quần dài", "quần ống", "quần suông", "quần tây",
);
const KW_QUAN = kw("quần");
const KW_AO = kw("áo");

export const CATEGORIES: readonly Category[] = [
  // ── Women (aloyoga.com › Women › Clothing) ──────────────────────
  {
    key: "matching-sets", group: "women", en: "Matching Sets", vi: "Set đồ",
    keywords: [kw("sets?", "bộ(?!\\s+sưu)", "combo", "(?:two|2|three|3)[- ]pieces?")],
  },
  {
    key: "tops", group: "women", en: "Tops", vi: "Áo & tank top",
    keywords: [
      kw(
        "tops?", "tanks?", "camis?", "camisoles?", "tees?", "t-shirts?", "shirts?", "blouses?", "halters?",
        "crops?", "cropped", "long[- ]?sleeves?", "tunics?", "polos?",
        "áo thun", "áo tank", "áo ba lỗ", "áo crop", "áo croptop",
      ),
    ],
    weakKeywords: [KW_AO],
  },
  { key: "sweatshirts-hoodies", group: "women", en: "Sweatshirts & Hoodies", vi: "Áo nỉ & hoodie", keywords: [KW_SWEATSHIRTS] },
  {
    key: "leggings", group: "women", en: "Leggings", vi: "Quần legging",
    keywords: [kw("leggings?", "tights", "capris?", "quần legging", "quần bó")],
  },
  { key: "jackets-coats", group: "women", en: "Jackets & Coats", vi: "Áo khoác", keywords: [KW_JACKETS] },
  { key: "pants", group: "women", en: "Pants & Trousers", vi: "Quần dài", keywords: [KW_PANTS], weakKeywords: [KW_QUAN] },
  {
    key: "sports-bras", group: "women", en: "Sports Bras", vi: "Áo bra thể thao",
    keywords: [kw("bras?", "bralettes?", "áo bra", "áo ngực", "áo lót thể thao")],
  },
  { key: "shorts", group: "women", en: "Shorts", vi: "Quần short", keywords: [KW_SHORTS] },
  {
    key: "skirts-skorts", group: "women", en: "Skirts & Skorts", vi: "Váy & skort",
    keywords: [kw("skorts?", "skirts?", "chân váy", "váy")],
  },
  { key: "sweatpants", group: "women", en: "Sweatpants", vi: "Quần nỉ & jogger", keywords: [KW_SWEATPANTS] },
  {
    key: "sweaters-knitwear", group: "women", en: "Sweaters & Knitwear", vi: "Áo len & dệt kim",
    keywords: [kw("sweaters?", "knits?", "knitwear", "knitted", "cardigans?", "áo len", "áo dệt kim")],
  },
  {
    key: "dresses", group: "women", en: "Dresses", vi: "Đầm",
    keywords: [kw("dress(?:es)?", "đầm", "váy liền", "váy đầm")],
  },
  {
    key: "onesies", group: "women", en: "Onesies", vi: "Bodysuit & onesie",
    keywords: [kw("bodysuits?", "onesies?", "jumpsuits?", "rompers?", "unitards?", "playsuits?", "áo liền quần")],
  },
  {
    key: "intimates", group: "women", en: "Intimates", vi: "Đồ lót",
    keywords: [kw("underwear", "intimates?", "panty", "panties", "briefs?", "thongs?", "boyshorts?", "đồ lót", "quần lót", "quần chip")],
  },

  // ── Men (aloyoga.com › Men › Clothing) ──────────────────────────
  { key: "mens-sweatshirts-hoodies", group: "men", en: "Sweatshirts & Hoodies", vi: "Áo nỉ & hoodie nam", keywords: [KW_SWEATSHIRTS] },
  {
    key: "mens-shirts", group: "men", en: "Shirts", vi: "Áo nam",
    keywords: [kw("shirts?", "tees?", "t-shirts?", "tanks?", "tops?", "polos?", "long[- ]?sleeves?", "henleys?", "áo thun", "áo polo", "áo ba lỗ", "áo tank")],
    weakKeywords: [KW_AO],
  },
  { key: "mens-shorts", group: "men", en: "Shorts", vi: "Quần short nam", keywords: [KW_SHORTS] },
  { key: "mens-pants", group: "men", en: "Pants", vi: "Quần dài nam", keywords: [KW_PANTS], weakKeywords: [KW_QUAN] },
  { key: "mens-sweatpants", group: "men", en: "Sweatpants", vi: "Quần nỉ nam", keywords: [KW_SWEATPANTS] },
  { key: "mens-jackets", group: "men", en: "Jackets", vi: "Áo khoác nam", keywords: [KW_JACKETS] },
  {
    key: "mens-boxers", group: "men", en: "Boxers", vi: "Quần boxer",
    keywords: [kw("boxers?", "boxer briefs?", "briefs?", "trunks?", "underwear", "quần lót", "quần boxer", "quần sịp")],
  },

  // ── Accessories (aloyoga.com › Accessories) ─────────────────────
  {
    key: "hats", group: "accessories", en: "Hats", vi: "Mũ & nón",
    keywords: [kw("hats?", "caps?", "visors?", "beanies?", "bucket hats?", "headwear", "mũ", "nón")],
  },
  {
    key: "bags", group: "accessories", en: "Bags", vi: "Túi",
    keywords: [kw("bags?", "totes?", "backpacks?", "pouch(?:es)?", "duffels?", "duffles?", "crossbody", "túi", "balo", "ba lô")],
  },
  {
    key: "yoga-mats-equipment", group: "accessories", en: "Yoga Mats & Equipment", vi: "Thảm yoga & dụng cụ",
    keywords: [
      kw(
        "yoga mats?", "mats?", "blocks?", "straps?", "foam rollers?", "rollers?", "dumbbells?", "kettlebells?",
        "resistance bands?", "bands?", "bottles?", "tumblers?", "towels?", "sliders?", "wheels?",
        "thảm yoga", "thảm", "gạch yoga", "dây tập", "bình nước", "khăn", "tạ",
      ),
    ],
  },
  { key: "socks", group: "accessories", en: "Socks", vi: "Tất", keywords: [kw("socks?", "tất(?!\\s+cả)", "vớ")] },
  {
    key: "hair-accessories", group: "accessories", en: "Hair Accessories", vi: "Phụ kiện tóc",
    keywords: [
      kw(
        "scrunchies?", "headbands?", "hair ?bands?", "hair ?ties?", "hair ?clips?", "hair ?claws?", "claw clips?", "hair", "barrettes?",
        "dây buộc tóc", "cột tóc", "băng đô", "kẹp tóc", "phụ kiện tóc",
      ),
    ],
  },
  {
    key: "sunglasses", group: "accessories", en: "Sunglasses", vi: "Kính mát",
    keywords: [kw("sunglasses", "sunnies", "shades", "eyewear", "kính mát", "kính râm", "kính")],
  },
  {
    key: "jewelry", group: "accessories", en: "Jewelry", vi: "Trang sức",
    keywords: [
      kw(
        "jewelry", "jewellery", "necklaces?", "bracelets?", "earrings?", "rings?", "anklets?", "pendants?",
        "trang sức", "vòng tay", "dây chuyền", "bông tai", "khuyên tai", "nhẫn",
      ),
    ],
  },
  {
    key: "gift-cards", group: "accessories", en: "Gift Cards", vi: "Thẻ quà tặng",
    keywords: [kw("gift ?cards?", "e-?gift", "vouchers?", "thẻ quà tặng", "thẻ quà", "phiếu quà tặng")],
  },

  // ── Shoes (aloyoga.com › Shoes) ─────────────────────────────────
  {
    key: "sneakers", group: "shoes", en: "Sneakers", vi: "Giày sneaker",
    keywords: [kw("sneakers?", "shoes?", "trainers?", "runners?", "footwear", "giày")],
  },
  {
    key: "slides", group: "shoes", en: "Slides", vi: "Dép",
    keywords: [kw("slides?", "sandals?", "flip[- ]?flops?", "slippers?", "mules?", "dép", "xăng đan")],
  },
];

const BY_KEY: ReadonlyMap<string, Category> = new Map(CATEGORIES.map((c) => [c.key, c]));

export function getCategory(key: string | null | undefined): Category | undefined {
  return key ? BY_KEY.get(key) : undefined;
}

export function isCategoryKey(value: unknown): value is string {
  return typeof value === "string" && BY_KEY.has(value);
}

/**
 * Lower-case, diacritics stripped, đ → d, apostrophes dropped ("Men's" →
 * "mens"), "&"/"and" removed, punctuation → single spaces. Makes a key, its
 * English label and its Vietnamese label all comparable:
 * "skirts-skorts" / "Skirts & Skorts" / "Skirts and Skorts" → "skirts skorts".
 */
function foldForMatch(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/['’]/g, "")
    .replace(/&/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\band\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function categoryMatches(c: Category, folded: string): boolean {
  return foldForMatch(c.key) === folded || foldForMatch(c.en) === folded || foldForMatch(c.vi) === folded;
}

/** "Men / Sweatshirts & Hoodies", "Nam › Áo nỉ", "men > hoodies", "Women | Tops". */
const GROUP_QUALIFIER_SEP = /\s*(?:\/|>|›|\||:)\s*/;

/**
 * Resolve a human-typed category — a CSV cell, a form value — to a key.
 * Accepts the key itself ("leggings"), the English label ("Sports Bras") or
 * the Vietnamese label with or without diacritics ("Quần legging",
 * "quan legging"), optionally qualified by its group ("Men / Shorts",
 * "Nam › Quần short") — needed because Women and Men share several labels.
 * An unqualified shared label resolves to the first match in taxonomy
 * order (Women before Men). null for blank / unknown input; callers decide
 * whether unknown is an error (the CSV imports report it per row and keep
 * the row).
 */
export function resolveCategoryKey(input: string | null | undefined): string | null {
  const raw = (input ?? "").trim();
  if (!raw) return null;
  if (BY_KEY.has(raw)) return raw;

  const parts = raw.split(GROUP_QUALIFIER_SEP).map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const groupFolded = foldForMatch(parts[0]);
    const group = CATEGORY_GROUPS.find(
      (g) => foldForMatch(g.key) === groupFolded || foldForMatch(g.en) === groupFolded || foldForMatch(g.vi) === groupFolded,
    );
    const labelFolded = foldForMatch(parts.slice(1).join(" "));
    if (group && labelFolded) {
      const hit = CATEGORIES.find((c) => c.group === group.key && categoryMatches(c, labelFolded));
      if (hit) return hit.key;
    }
  }

  const folded = foldForMatch(raw);
  if (!folded) return null;
  return CATEGORIES.find((c) => categoryMatches(c, folded))?.key ?? null;
}

export function getCategoryGroup(key: CategoryGroupKey): CategoryGroup {
  return CATEGORY_GROUPS.find((g) => g.key === key)!;
}

/** Localised label; `""` for null/unknown keys so callers can `||` a fallback. */
export function categoryLabel(key: string | null | undefined, locale: string | null | undefined): string {
  const c = getCategory(key);
  if (!c) return "";
  return locale === "vi" ? c.vi : c.en;
}

/** Categories grouped in display order — for optgroups / grouped menus. */
export function categoriesByGroup(): Array<{ group: CategoryGroup; categories: Category[] }> {
  return CATEGORY_GROUPS.map((group) => ({
    group,
    categories: CATEGORIES.filter((c) => c.group === group.key),
  }));
}

// ─── Auto-suggest ───────────────────────────────────────────────────
//
// Priority order for the specific pass: most specific garment words first,
// generic "top" words last. A title like "Halter Bra" must resolve to
// sports-bras (not tops), "Crop Leggings" to leggings (not tops), and any
// title containing "Set" to matching-sets (a bra + legging set is a set).
const SUGGEST_ORDER: readonly string[] = [
  "matching-sets",
  "sports-bras",
  "leggings",
  "dresses",
  "skirts-skorts",
  "shorts",
  "onesies",
  "intimates",
  "sweatshirts-hoodies",
  "sweatpants",
  "jackets-coats",
  "sweaters-knitwear",
  "pants",
  "socks",
  "hats",
  "bags",
  "hair-accessories",
  "yoga-mats-equipment",
  "sunglasses",
  "jewelry",
  "gift-cards",
  "slides",
  "sneakers",
  "tops",
];

// Men's categories are only tried when the title says so ("Men's", "for
// men", Vietnamese "nam" — but not "Việt Nam"). EQL is a women's brand, so
// a bare "Training Shorts" is women's shorts.
const MENS_RE = /(?<![\p{L}])(?:men'?s|mens|man'?s|for men|male|(?<!vi[eệ]t\s)nam)(?![\p{L}])/iu;
const MENS_ORDER: readonly string[] = [
  "mens-boxers",
  "mens-sweatshirts-hoodies",
  "mens-sweatpants",
  "mens-jackets",
  "mens-shorts",
  "mens-pants",
  "mens-shirts",
];

export const SUGGEST_CONFIDENCE = {
  /** A specific garment word matched ("Leggings", "Bra", "Skort"…). */
  specific: 0.9,
  /** Only a generic word matched ("áo", "quần") — needs a human look. */
  weak: 0.5,
  /** `productType` already names a category ("Sports Bras", "Sets"). */
  productType: 0.95,
} as const;

export interface CategorySuggestion {
  key: string;
  /** 0–1. ≥ 0.9 = confident; 0.5 = generic-word fallback, review it. */
  confidence: number;
  source?: "title" | "titleEn" | "productType";
}

function runPass(title: string, order: readonly string[], weak: boolean): string | null {
  for (const key of order) {
    const cat = BY_KEY.get(key)!;
    const patterns = weak ? cat.weakKeywords : cat.keywords;
    if (patterns?.some((re) => re.test(title))) return key;
  }
  return null;
}

/**
 * Suggest a category from a product title (EN or VI). First specific match
 * wins; generic "top/áo" and "quần" only when nothing else matched.
 * Returns null when no keyword matches at all — leave those for a human.
 */
export function suggestCategoryForTitle(title: string | null | undefined): CategorySuggestion | null {
  const t = (title ?? "").trim().toLowerCase();
  if (!t) return null;
  const mens = MENS_RE.test(t);

  const specific = (mens && runPass(t, MENS_ORDER, false)) || runPass(t, SUGGEST_ORDER, false);
  if (specific) return { key: specific, confidence: SUGGEST_CONFIDENCE.specific, source: "title" };

  const weak = (mens && runPass(t, MENS_ORDER, true)) || runPass(t, SUGGEST_ORDER, true);
  if (weak) return { key: weak, confidence: SUGGEST_CONFIDENCE.weak, source: "title" };

  return null;
}

/**
 * Product-level suggestion: a `productType` that already names a category
 * ("Sports Bras", "Leggings", "Sets") is the strongest signal, then the
 * title, then the curated English title. Merchandise-line productTypes
 * ("Cozy", "Focus") match nothing and fall through to the title.
 */
export function suggestCategoryForProduct(p: {
  title: string | null | undefined;
  titleEn?: string | null;
  productType?: string | null;
}): CategorySuggestion | null {
  if (p.productType) {
    const fromType = suggestCategoryForTitle(p.productType);
    if (fromType && fromType.confidence >= SUGGEST_CONFIDENCE.specific) {
      return { key: fromType.key, confidence: SUGGEST_CONFIDENCE.productType, source: "productType" };
    }
  }
  const fromTitle = suggestCategoryForTitle(p.title);
  if (fromTitle && fromTitle.confidence >= SUGGEST_CONFIDENCE.specific) return fromTitle;
  const fromTitleEn = p.titleEn ? suggestCategoryForTitle(p.titleEn) : null;
  if (fromTitleEn && fromTitleEn.confidence >= SUGGEST_CONFIDENCE.specific) return { ...fromTitleEn, source: "titleEn" };
  return fromTitle ?? (fromTitleEn ? { ...fromTitleEn, source: "titleEn" } : null);
}
